import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "./WorkingDays.css";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Switch } from "@mui/material";
import { toast } from "react-toastify";

const WorkingDays = () => {
  const { user } = useSelector((state) => state.auth);

  const token = user?.token;

  const newBaseUrl = "https://estbn.com/EstbnNew/public";

  const headers = { Authorization: "Bearer " + token };

  const { id } = useParams();

  const [weekDays, setWeekDays] = useState([]);

  const [workingDays, setWorkingDays] = useState([]);

  const [openDayId, setOpenDayId] = useState(null);

  const [timeRows, setTimeRows] = useState({});

  const [loadingTimes, setLoadingTimes] = useState({});

  // get all weekdays
  const getWeekDays = async () => {
    try {
      const res = await axios.get(`${newBaseUrl}/api/portal/working_days`, {
        headers,
      });
      setWeekDays(res.data.data.working_days);
    } catch (error) {
      console.error("getWeekDays error:", error);
    }
  };

  // get working days for this vehicle
  const getWorkingDays = async () => {
    try {
      const res = await axios.get(
        `${newBaseUrl}/api/portal/vehicle_working_days?vehicle_id=${id}`,
        { headers }
      );
      setWorkingDays(res.data.data.working_days);
    } catch (error) {
      console.error("getWorkingDays error:", error);
    }
  };

  const workingDayIds = new Set(workingDays.map((d) => String(d.day_id)));

  const findWorkingDayRecord = (dayId) =>
    workingDays.find((wd) => String(wd.day_id) === String(dayId));

  // Load times for a specific day
  const loadTimesForDay = async (dayId) => {
    setLoadingTimes((prev) => ({ ...prev, [dayId]: true }));
    try {
      const workingDayRecord = findWorkingDayRecord(dayId);
      if (!workingDayRecord) {
        setTimeRows((prev) => ({
          ...prev,
          [dayId]: [{ from: "", to: "" }],
        }));
        setLoadingTimes((prev) => ({ ...prev, [dayId]: false }));
        return;
      }

      const res = await axios.get(
        `${newBaseUrl}/api/portal/vehicle_day_times?day_id=${workingDayRecord.id}`,
        { headers }
      );

      const times =
        res.data?.vehicle_day_times || res.data?.data?.vehicle_day_times || [];

      setTimeRows((prev) => ({
        ...prev,
        [dayId]: [
          ...times.map((t) => ({ id: t.id, from: t.from, to: t.to })),
          { from: "", to: "" },
        ],
      }));
    } catch (err) {
      console.error("Error loading times for day", dayId, err);
      setTimeRows((prev) => ({
        ...prev,
        [dayId]: [{ from: "", to: "" }],
      }));
    } finally {
      setLoadingTimes((prev) => ({ ...prev, [dayId]: false }));
    }
  };

  // open time details
  const toggleForm = (dayId) => {
    if (!workingDayIds.has(String(dayId))) return;
    if (openDayId === dayId) {
      setOpenDayId(null);
    } else {
      setOpenDayId(dayId);
      loadTimesForDay(dayId);
    }
  };

  // handle times form
  const handleTimeChange = (dayId, rowIndex, field, value) => {
    setTimeRows((prev) => {
      const rows = [...(prev[dayId] || [{ from: "", to: "" }])];
      rows[rowIndex] = { ...rows[rowIndex], [field]: value };
      return { ...prev, [dayId]: rows };
    });
  };

  // add times
  const addTimeRow = async (dayId, fromValue, toValue) => {
    if (!fromValue || !toValue) {
      toast.error("please select time from & to", { autoClose: 2000 });
      return;
    }
    const workingDayRecord = findWorkingDayRecord(dayId);
    if (!workingDayRecord) {
      toast.error("error , the day log not found", { autoClose: 2000 });
      return;
    }
    const formData = new FormData();
    formData.append("day_id", workingDayRecord.id);
    formData.append("from", fromValue);
    formData.append("to", toValue);
    try {
      await axios.post(`${newBaseUrl}/api/portal/vehicle_day_times`, formData, {
        headers,
      });
      toast.success("Time added successfully", { autoClose: 1500 });
      await loadTimesForDay(dayId);
    } catch (err) {
      console.error("addTimeRow error:", err);
      toast.error("error to add time", { autoClose: 2000 });
    }
  };

  // delete times
  const deleteTimeRow = async (dayId, timeId) => {
    try {
      await axios.delete(
        `${newBaseUrl}/api/portal/vehicle_day_times/${timeId}`,
        { headers }
      );
      toast.warning("Time deleted successfully", { autoClose: 1500 });
      await loadTimesForDay(dayId);
    } catch (err) {
      console.error("deleteTimeRow error:", err);
      toast.error("can't delete time", { autoClose: 2000 });
    }
  };

  // toggle up & down
  const handleToggle = async (dayId) => {
    const isOn = workingDayIds.has(String(dayId));
    try {
      if (isOn) {
        const workingDay = findWorkingDayRecord(dayId);
        if (!workingDay) return;
        await axios.delete(
          `${newBaseUrl}/api/portal/vehicle_working_days/${workingDay.id}`,
          { headers }
        );
        setWorkingDays((prev) => prev.filter((wd) => wd.id !== workingDay.id));
        toast.success("Day deleted successfully", { autoClose: 2000 });
        if (openDayId === dayId) setOpenDayId(null);
      } else {
        const dayForm = new FormData();
        dayForm.append("vehicle_id", id);
        dayForm.append("day_id", dayId);
        await axios.post(
          `${newBaseUrl}/api/portal/vehicle_working_days`,
          dayForm,
          { headers }
        );
        await getWorkingDays();
        toast.success(" Day added successfully", { autoClose: 2000 });
      }
    } catch (error) {
      console.error("handleToggle error:", error);
      toast.error("error to toggle", { autoClose: 2000 });
      getWorkingDays();
    }
  };

  // effects
  useEffect(() => {
    getWeekDays();
    getWorkingDays();
  }, []);

  return (
    <div className="col-md-12">
      <div
        className="card p-3 my-3 mr-5 working-days-card"
        style={{ border: "2px solid #EAEAEA" }}
      >
        <h3>Working Days</h3>
        <div>
          {/* days of the weeek part */}
          {weekDays.map((day) => {
            const isOn = workingDayIds.has(String(day.id));
            return (
              <div key={day.id} className="border border-1 rounded-2 p-3 my-3">
                <div className="d-flex align-items-center justify-content-between working-days-body">
                  <div className="d-flex align-items-center">
                    <Switch
                      checked={isOn}
                      onChange={() => handleToggle(day.id)}
                      size="small"
                    />
                    <p className="fw-semibold my-0 mx-2">{day.name_en}</p>
                  </div>
                  <div>
                    {openDayId === day.id ? (
                      <ChevronUp
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleForm(day.id)}
                      />
                    ) : (
                      <ChevronDown
                        style={{
                          cursor: isOn ? "pointer" : "not-allowed",
                          opacity: isOn ? 1 : 0.4,
                        }}
                        onClick={() => toggleForm(day.id)}
                      />
                    )}
                  </div>
                </div>
                {/* times part  */}
                {openDayId === day.id && (
                  <div className="mt-3">
                    {loadingTimes[day.id] ? (
                      <div>Loading Times</div>
                    ) : (
                      <>
                        {timeRows[day.id]?.map((row, index) => {
                          const isLast = index === timeRows[day.id].length - 1;
                          return (
                            <div
                              key={index}
                              className="d-flex align-items-center mb-2 gap-2 working-hours-inputs"
                            >
                              <input
                                type="time"
                                value={row.from || ""}
                                onChange={(e) =>
                                  handleTimeChange(
                                    day.id,
                                    index,
                                    "from",
                                    e.target.value
                                  )
                                }
                                className="form-control"
                              />
                              <input
                                type="time"
                                value={row.to || ""}
                                onChange={(e) =>
                                  handleTimeChange(
                                    day.id,
                                    index,
                                    "to",
                                    e.target.value
                                  )
                                }
                                className="form-control"
                              />
                              {row.id ? (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => deleteTimeRow(day.id, row.id)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              ) : (
                                isLast && (
                                  <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() =>
                                      addTimeRow(day.id, row.from, row.to)
                                    }
                                  >
                                    <Plus size={16} />
                                  </button>
                                )
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkingDays;
